import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
} from '@openzeppelin/ui-components';
import { cn } from '@openzeppelin/ui-utils';
import { Icon } from '@ascend/shared/components';
import { useWalletStore } from '../stores/useWalletStore';
import {
  DataTable,
  type DataTableColumn,
  SearchBar,
  PaginationControls,
  TABLE_PAGE_SIZE_OPTIONS,
  type SortDirection,
} from '../components/ListControls';
import {
  CLIENT_LENDING_POOLS,
  type ClientLendingPool,
} from '../data/mockAdapter';

// ─── Types ────────────────────────────────────────────────────────────────────

type PoolSortKey = 'name' | 'deposited' | 'lpTokens' | 'apy' | 'earnedYield' | 'currentValue' | 'withdrawable';
type PublicPoolSortKey = 'name' | 'apy' | 'tvl' | 'utilization';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parsePercentValue(value: string): number {
  const normalized = value.replace('%', '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseCompactCurrencyValue(value: string): number {
  const normalized = value.replace(/[$,\s]/g, '').toUpperCase();
  const factor = normalized.endsWith('B')
    ? 1_000_000_000
    : normalized.endsWith('M')
      ? 1_000_000
      : normalized.endsWith('K')
        ? 1_000
        : 1;
  const numeric = factor === 1 ? normalized : normalized.slice(0, -1);
  const parsed = Number(numeric);
  if (!Number.isFinite(parsed)) return 0;
  return parsed * factor;
}

function getWithdrawableColor(pct: number): string {
  if (pct >= 100) return 'text-ascend-success';
  if (pct >= 50) return 'text-amber-600';
  return 'text-ascend-error';
}

// ─── Section Definitions ─────────────────────────────────────────────────────


// ─── Derived Stats ───────────────────────────────────────────────────────────

const totalDeposited = CLIENT_LENDING_POOLS.reduce((s, p) => s + p.depositedUsd, 0);
const totalEarned = CLIENT_LENDING_POOLS.reduce((s, p) => s + p.earnedYieldUsd, 0);
const totalCurrentValue = CLIENT_LENDING_POOLS.reduce((s, p) => s + p.currentValueUsd, 0);
const avgApy = CLIENT_LENDING_POOLS.length > 0
  ? CLIENT_LENDING_POOLS.reduce((s, p) => s + p.apyPct, 0) / CLIENT_LENDING_POOLS.length
  : 0;

// ─── Authenticated View ──────────────────────────────────────────────────────

function AuthenticatedDashboard() {
  const navigate = useNavigate();

  const [poolSearch, setPoolSearch] = useState('');
  const [poolSortKey, setPoolSortKey] = useState<PoolSortKey>('currentValue');
  const [poolSortDirection, setPoolSortDirection] = useState<SortDirection>('desc');
  const [poolPage, setPoolPage] = useState(1);
  const [poolPageSize, setPoolPageSize] = useState<number>(TABLE_PAGE_SIZE_OPTIONS[0]);

  // Reset to page 1 when sort/search changes. Comparing prev state during
  // render is the React-recommended pattern for derived state resets.
  const poolFilterKey = `${poolSearch}|${poolSortKey}|${poolSortDirection}`;
  const [prevPoolFilterKey, setPrevPoolFilterKey] = useState(poolFilterKey);
  if (prevPoolFilterKey !== poolFilterKey) {
    setPrevPoolFilterKey(poolFilterKey);
    setPoolPage(1);
  }


  const filteredPools = useMemo(() => {
    if (!poolSearch.trim()) return CLIENT_LENDING_POOLS;
    const q = poolSearch.toLowerCase();
    return CLIENT_LENDING_POOLS.filter((p) => p.name.toLowerCase().includes(q));
  }, [poolSearch]);

  const sortedPools = useMemo(() => {
    const items = [...filteredPools];
    items.sort((a, b) => {
      let comparison = 0;
      switch (poolSortKey) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'deposited':
          comparison = a.depositedUsd - b.depositedUsd;
          break;
        case 'lpTokens':
          comparison = a.lpTokensRaw - b.lpTokensRaw;
          break;
        case 'apy':
          comparison = a.apyPct - b.apyPct;
          break;
        case 'earnedYield':
          comparison = a.earnedYieldUsd - b.earnedYieldUsd;
          break;
        case 'currentValue':
          comparison = a.currentValueUsd - b.currentValueUsd;
          break;
        case 'withdrawable':
          comparison = a.withdrawablePct - b.withdrawablePct;
          break;
      }
      return poolSortDirection === 'asc' ? comparison : -comparison;
    });
    return items;
  }, [filteredPools, poolSortDirection, poolSortKey]);

  const paginatedPools = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(sortedPools.length / poolPageSize));
    const safe = Math.min(poolPage, totalPages);
    return sortedPools.slice((safe - 1) * poolPageSize, safe * poolPageSize);
  }, [poolPage, poolPageSize, sortedPools]);


  function onSortPools(nextKey: PoolSortKey) {
    setPoolSortDirection((prev) => (
      poolSortKey === nextKey ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'
    ));
    setPoolSortKey(nextKey);
  }


  const poolColumns = useMemo<DataTableColumn<ClientLendingPool, PoolSortKey>[]>(
    () => [
      {
        id: 'name',
        label: 'Pool',
        sortable: true,
        sortKey: 'name',
        renderCell: (pool) => <span className="font-medium">{pool.name}</span>,
      },
      {
        id: 'deposited',
        label: 'Deposited',
        align: 'right',
        sortable: true,
        sortKey: 'deposited',
        renderCell: (pool) => pool.deposited,
      },
      {
        id: 'lpTokens',
        label: 'LP-Tokens',
        align: 'right',
        sortable: true,
        sortKey: 'lpTokens',
        renderCell: (pool) => pool.lpTokens,
      },
      {
        id: 'apy',
        label: 'APY',
        align: 'center',
        sortable: true,
        sortKey: 'apy',
        renderCell: (pool) => (
          <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
            {pool.apy}
          </span>
        ),
      },
      {
        id: 'earnedYield',
        label: 'Earned Yield',
        align: 'right',
        sortable: true,
        sortKey: 'earnedYield',
        renderCell: (pool) => <span className="text-ascend-success font-medium">{pool.earnedYield}</span>,
      },
      {
        id: 'currentValue',
        label: 'Current Value',
        align: 'right',
        sortable: true,
        sortKey: 'currentValue',
        renderCell: (pool) => <span className="font-semibold">{pool.currentValue}</span>,
      },
      {
        id: 'withdrawable',
        label: 'Withdrawable',
        align: 'center',
        sortable: true,
        sortKey: 'withdrawable',
        renderCell: (pool) => (
          <span className={cn('font-medium', getWithdrawableColor(pool.withdrawablePct))}>
            {pool.withdrawable}
          </span>
        ),
      },
      {
        id: 'actions',
        label: 'Actions',
        align: 'center',
        renderCell: (pool) => (
          <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => navigate(`/lender/deposit?poolId=${pool.id}`)}
              className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <Icon name="streamline:interface-add-1-expand-cross-buttons-button-more-remove-plus-add" className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => navigate(`/lender/withdraw?poolId=${pool.id}`)}
              className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <Icon name="streamline:interface-file-remove-file-common-remove-minus-subtract" className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
      },
    ],
    [navigate],
  );

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <Icon name="streamline:interface-dashboard-layout-3-app-application-dashboard-home-layout" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Lender Dashboard</h1>
            <p className="text-sm text-muted-foreground">
              View your lending positions and earned yield.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => navigate('/lender/deposit')}>
            <Icon name="streamline:interface-arrows-button-to-bottom-arrow-down-line-to-bottom" className="mr-2 h-4 w-4" />
            Deposit Stablecoins
          </Button>
          <Button variant="outline" onClick={() => navigate('/lender/withdraw')}>
            <Icon name="streamline:interface-arrows-button-to-top-arrow-up-line-to-top" className="mr-2 h-4 w-4" />
            Withdraw Stablecoins
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div id="overview" className="scroll-mt-28 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        <StatCard
          title="Total Deposited"
          value={`$${totalDeposited.toLocaleString()}`}
          subtitle="Principal amount"
          icon={<Icon name="streamline:piggy-bank" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Total Earned"
          value={`$${totalEarned.toLocaleString()}`}
          subtitle="Yield accrued"
          icon={<Icon name="streamline:graph-bar-increase" className="h-5 w-5 text-ascend-success" />}
        />
        <StatCard
          title="Current Value"
          value={`$${totalCurrentValue.toLocaleString()}`}
          subtitle="Principal + yield"
          icon={<Icon name="streamline:money-graph-bar-product-data-bars-analysis-analytics-graph-business-chart" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Average APY"
          value={`${avgApy.toFixed(2)}%`}
          subtitle="Across all pools"
          icon={<Icon name="streamline:discount-percent-circle" className="h-5 w-5 text-muted-foreground" />}
        />
      </div>

      {/* Reserve & Protection Status */}
      <Card id="reserve-protection" className="scroll-mt-28">
        <CardHeader className="p-6">
          <CardTitle>Reserve &amp; Protection Status</CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg bg-muted/50 p-4">
              <p className="text-sm text-muted-foreground">Total Reserve</p>
              <p className="mt-1 text-2xl font-bold">$2.5M</p>
              <p className="mt-0.5 text-xs text-ascend-success font-medium">+5% this month</p>
            </div>
            <div className="rounded-lg bg-muted/50 p-4">
              <p className="text-sm text-muted-foreground">Available Capacity</p>
              <p className="mt-1 text-2xl font-bold">$2.1M</p>
              <p className="mt-0.5 text-xs text-muted-foreground">84% available</p>
            </div>
            <div className="rounded-lg bg-muted/50 p-4">
              <p className="text-sm text-muted-foreground">Committed</p>
              <p className="mt-1 text-2xl font-bold">$400K</p>
              <p className="mt-0.5 text-xs text-muted-foreground">16% in use</p>
            </div>
            <div className="rounded-lg bg-muted/50 p-4">
              <p className="text-sm text-muted-foreground">Coverage Ratio</p>
              <p className="mt-1 text-2xl font-bold">8.2x</p>
              <p className="mt-0.5 text-xs text-ascend-success font-medium">Excellent</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Your Positions */}
      <Card id="positions" className="scroll-mt-28">
        <CardHeader className="p-6">
          <CardTitle>Your Positions</CardTitle>
          <CardDescription>Active lending positions across pools. Click a row to see details.</CardDescription>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          <SearchBar
            value={poolSearch}
            onChange={setPoolSearch}
            placeholder="Search pools..."
            className="mb-4 sm:max-w-sm"
          />

          {/* Desktop table */}
          <div className="hidden sm:block">
            <DataTable<ClientLendingPool, PoolSortKey>
              columns={poolColumns}
              rows={paginatedPools}
              rowKey={(pool) => pool.id}
              sort={{
                key: poolSortKey,
                direction: poolSortDirection,
                onSort: onSortPools,
              }}
              emptyMessage={`No pools matching "${poolSearch}"`}
              onRowClick={(pool) => navigate(`/lender/position/${pool.id}`)}
            />
          </div>

          {/* Mobile stacked cards */}
          <div className="sm:hidden space-y-3">
            {paginatedPools.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-12 text-center">
                <p className="text-sm text-muted-foreground">No pools matching &ldquo;{poolSearch}&rdquo;</p>
              </div>
            ) : (
              paginatedPools.map((pool) => (
                <div
                  key={pool.id}
                  className="rounded-lg border border-border p-4 space-y-3 cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() => navigate(`/lender/position/${pool.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{pool.name}</span>
                    <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                      {pool.apy}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Deposited</span>
                      <div className="font-semibold">{pool.deposited}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">LP-Tokens</span>
                      <div className="font-semibold">{pool.lpTokens}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Earned Yield</span>
                      <div className="font-semibold text-ascend-success">{pool.earnedYield}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Current Value</span>
                      <div className="font-semibold">{pool.currentValue}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Withdrawable</span>
                      <div className={cn('font-semibold', getWithdrawableColor(pool.withdrawablePct))}>
                        {pool.withdrawable}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" className="flex-1" onClick={() => navigate(`/lender/deposit?poolId=${pool.id}`)}>
                      <Icon name="streamline:interface-add-1-expand-cross-buttons-button-more-remove-plus-add" className="mr-1 h-3 w-3" /> Deposit
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => navigate(`/lender/withdraw?poolId=${pool.id}`)}>
                      <Icon name="streamline:interface-file-remove-file-common-remove-minus-subtract" className="mr-1 h-3 w-3" /> Withdraw
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
          <PaginationControls
            page={poolPage}
            setPage={setPoolPage}
            pageSize={poolPageSize}
            setPageSize={setPoolPageSize}
            totalItems={filteredPools.length}
            label="pools"
          />
        </CardContent>
      </Card>

    </div>
  );
}

// ─── Public View ─────────────────────────────────────────────────────────────

function PublicDashboard() {
  const { connect } = useWalletStore();
  const [poolSearch, setPoolSearch] = useState('');
  const [poolSortKey, setPoolSortKey] = useState<PublicPoolSortKey>('tvl');
  const [poolSortDirection, setPoolSortDirection] = useState<SortDirection>('desc');
  const [poolPage, setPoolPage] = useState(1);
  const [poolPageSize, setPoolPageSize] = useState<number>(TABLE_PAGE_SIZE_OPTIONS[0]);

  // Reset to page 1 when sort/search changes.
  const publicPoolFilterKey = `${poolSearch}|${poolSortKey}|${poolSortDirection}`;
  const [prevPublicPoolFilterKey, setPrevPublicPoolFilterKey] = useState(publicPoolFilterKey);
  if (prevPublicPoolFilterKey !== publicPoolFilterKey) {
    setPrevPublicPoolFilterKey(publicPoolFilterKey);
    setPoolPage(1);
  }

  const filteredPools = useMemo(() => {
    if (!poolSearch.trim()) return CLIENT_LENDING_POOLS;
    const q = poolSearch.toLowerCase();
    return CLIENT_LENDING_POOLS.filter((p) => p.name.toLowerCase().includes(q));
  }, [poolSearch]);

  const sortedPools = useMemo(() => {
    const items = [...filteredPools];
    items.sort((a, b) => {
      let comparison = 0;
      switch (poolSortKey) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'apy':
          comparison = a.apyPct - b.apyPct;
          break;
        case 'tvl':
          comparison = parseCompactCurrencyValue(a.tvl) - parseCompactCurrencyValue(b.tvl);
          break;
        case 'utilization':
          comparison = parsePercentValue(a.utilization) - parsePercentValue(b.utilization);
          break;
      }
      return poolSortDirection === 'asc' ? comparison : -comparison;
    });
    return items;
  }, [filteredPools, poolSortDirection, poolSortKey]);

  const paginatedPools = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(sortedPools.length / poolPageSize));
    const safe = Math.min(poolPage, totalPages);
    return sortedPools.slice((safe - 1) * poolPageSize, safe * poolPageSize);
  }, [poolPage, poolPageSize, sortedPools]);

  function onSortPools(nextKey: PublicPoolSortKey) {
    setPoolSortDirection((prev) => (
      poolSortKey === nextKey ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'
    ));
    setPoolSortKey(nextKey);
    setPoolPage(1);
  }

  const publicPoolColumns = useMemo<DataTableColumn<ClientLendingPool, PublicPoolSortKey>[]>(
    () => [
      {
        id: 'name',
        label: 'Pool',
        sortable: true,
        sortKey: 'name',
        renderCell: (pool) => <span className="font-medium">{pool.name}</span>,
      },
      {
        id: 'apy',
        label: 'APY',
        align: 'center',
        sortable: true,
        sortKey: 'apy',
        renderCell: (pool) => (
          <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
            {pool.apy}
          </span>
        ),
      },
      {
        id: 'tvl',
        label: 'TVL',
        align: 'right',
        sortable: true,
        sortKey: 'tvl',
        renderCell: (pool) => <span className="font-semibold">{pool.tvl}</span>,
      },
      {
        id: 'utilization',
        label: 'Utilization',
        align: 'right',
        sortable: true,
        sortKey: 'utilization',
        renderCell: (pool) => pool.utilization,
      },
    ],
    [],
  );

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Hero */}
      <div id="hero" className="scroll-mt-28 rounded-2xl border border-border bg-gradient-to-br from-primary/5 via-background to-primary/5 p-8 sm:p-10">
        <div className="flex items-start gap-3">
          <Icon name="streamline:interface-dashboard-layout-3-app-application-dashboard-home-layout" className="mt-1 h-7 w-7 text-foreground flex-shrink-0" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Lend on Ascend</h1>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              Earn competitive yield by depositing stablecoins into institutional-grade lending pools.
              Your deposits are protected by the DDF reserve and governed by transparent, on-chain rules.
            </p>
          </div>
        </div>
        <Button onClick={connect} className="mt-6">
          <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="mr-2 h-4 w-4" />
          Connect Wallet to Start Lending
        </Button>
      </div>

      {/* How It Works */}
      <Card id="how-it-works" className="scroll-mt-28">
        <CardHeader className="p-6">
          <CardTitle>How It Works</CardTitle>
          <CardDescription>Start earning yield in three simple steps.</CardDescription>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-lg border border-border p-5 space-y-2">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">1</div>
                <h4 className="font-semibold">Deposit Stablecoins</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Deposit USDC, USDT, or DAI into lending pools.
              </p>
            </div>
            <div className="rounded-lg border border-border p-5 space-y-2">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">2</div>
                <h4 className="font-semibold">Earn Yield</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Earn competitive APY from borrower interest payments.
              </p>
            </div>
            <div className="rounded-lg border border-border p-5 space-y-2">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">3</div>
                <h4 className="font-semibold">Withdraw Anytime</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Flexible withdrawals with no lock-up period.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Protocol stat cards */}
      <div id="protocol-stats" className="scroll-mt-28 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        <StatCard
          title="Total TVL"
          value="$125.5M"
          subtitle="Across all pools"
          icon={<Icon name="streamline:money-cash-coins-stack-accounting-billing-payment-stack-cash-coins-currency-money-finance" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Total Lenders"
          value="842"
          subtitle="Active participants"
          icon={<Icon name="streamline:interface-user-multiple-close-geometric-human-multiple-person-up-user" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Average APY"
          value="7.85%"
          subtitle="Across all pools"
          icon={<Icon name="streamline:discount-percent-circle" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Total Yield Distributed"
          value="$12.4M"
          subtitle="To lenders since launch"
          icon={<Icon name="streamline:coins-stack" className="h-5 w-5 text-ascend-success" />}
        />
      </div>

      {/* Reserve & Protection Status */}
      <Card id="reserve-protection" className="scroll-mt-28">
        <CardHeader className="p-6">
          <CardTitle>Reserve &amp; Protection Status</CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg bg-muted/50 p-4">
              <p className="text-sm text-muted-foreground">Total Reserve</p>
              <p className="mt-1 text-2xl font-bold">$2.5M</p>
              <p className="mt-0.5 text-xs text-ascend-success font-medium">+5% this month</p>
            </div>
            <div className="rounded-lg bg-muted/50 p-4">
              <p className="text-sm text-muted-foreground">Available Capacity</p>
              <p className="mt-1 text-2xl font-bold">$2.1M</p>
              <p className="mt-0.5 text-xs text-muted-foreground">84% available</p>
            </div>
            <div className="rounded-lg bg-muted/50 p-4">
              <p className="text-sm text-muted-foreground">Committed</p>
              <p className="mt-1 text-2xl font-bold">$400K</p>
              <p className="mt-0.5 text-xs text-muted-foreground">16% in use</p>
            </div>
            <div className="rounded-lg bg-muted/50 p-4">
              <p className="text-sm text-muted-foreground">Coverage Ratio</p>
              <p className="mt-1 text-2xl font-bold">8.2x</p>
              <p className="mt-0.5 text-xs text-ascend-success font-medium">Excellent</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Available Lending Pools */}
      <Card id="lending-pools" className="scroll-mt-28">
        <CardHeader className="p-6">
          <CardTitle>Available Lending Pools</CardTitle>
          <CardDescription>Explore lending pools, their yields, and liquidity.</CardDescription>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          <SearchBar
            value={poolSearch}
            onChange={setPoolSearch}
            placeholder="Search pools..."
            className="mb-4 sm:max-w-sm"
          />

          {/* Desktop table */}
          <div className="hidden sm:block">
            <DataTable<ClientLendingPool, PublicPoolSortKey>
              columns={publicPoolColumns}
              rows={paginatedPools}
              rowKey={(pool) => pool.id}
              sort={{
                key: poolSortKey,
                direction: poolSortDirection,
                onSort: onSortPools,
              }}
              emptyMessage={`No pools matching "${poolSearch}"`}
            />
          </div>

          {/* Mobile stacked cards */}
          <div className="sm:hidden space-y-3">
            {paginatedPools.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-12 text-center">
                <p className="text-sm text-muted-foreground">No pools matching &ldquo;{poolSearch}&rdquo;</p>
              </div>
            ) : (
              paginatedPools.map((pool) => (
                <div key={pool.id} className="rounded-lg border border-border p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{pool.name}</span>
                    <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                      {pool.apy}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">TVL</span>
                      <div className="font-semibold">{pool.tvl}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Utilization</span>
                      <div className="font-semibold">{pool.utilization}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <PaginationControls
            page={poolPage}
            setPage={setPoolPage}
            pageSize={poolPageSize}
            setPageSize={setPoolPageSize}
            totalItems={filteredPools.length}
            label="pools"
          />
        </CardContent>
      </Card>

    </div>
  );
}

// ─── Main Export ─────────────────────────────────────────────────────────────

export function LenderDashboardPage() {
  const { isConnected } = useWalletStore();
  return isConnected ? <AuthenticatedDashboard /> : <PublicDashboard />;
}

// ─── Helper Components ────────────────────────────────────────────────────────

function StatCard({
  title,
  value,
  subtitle,
  icon,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm text-muted-foreground">{title}</span>
        {icon}
      </div>
      <div className="mt-2 text-2xl sm:text-3xl font-bold">{value}</div>
      {subtitle && <div className="mt-1 text-xs sm:text-sm text-muted-foreground">{subtitle}</div>}
    </div>
  );
}
